<div class="time-off-details-dialog">
  <div class="dialog-header">
    <h2 class="dialog-title">Manage Time Off Request</h2>
    <button
      mat-icon-button
      disableRipple
      class="dialog-close-button"
      [disabled]="isEditBusy"
      (click)="close()"
      aria-label="Close"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    <div class="request-summary" [class.is-editing]="isEditing">
      @if (data.employeeName || canEdit) {
        <div class="employee-row">
          @if (data.employeeName) {
            <div class="employee-name">{{ data.employeeName }}</div>
          }
          @if (canEdit && !isEditing) {
            <button
              mat-icon-button
              class="edit-button"
              matTooltip="Edit time off"
              aria-label="Edit time off"
              (click)="startEdit()"
            >
              <mat-icon>edit</mat-icon>
            </button>
          }
        </div>
      }

      @if (isEditing) {
        <form class="edit-form" @slideDown (ngSubmit)="saveEdit()">
          <div class="edit-toolbar">
            @if (data.typeName) {
              <span class="meta-chip type-chip">{{ data.typeName }}</span>
            }
            <span class="edit-toolbar-status">
              @if (isLoadingHolidays) {
                <span class="edit-hint">Checking holidays…</span>
              }
              <span
                class="day-count"
                [class.day-count-changed]="
                  selectedDayCount !== originalDayCount
                "
                >{{ dayCountLabel }}</span
              >
            </span>
          </div>

          <div class="day-picker">
            <ul class="day-legend" aria-label="Day picker legend">
              <li>
                <span class="legend-swatch swatch-kept"></span>This Request
              </li>
              <li><span class="legend-swatch swatch-removed"></span>Removed</li>
              <li><span class="legend-swatch swatch-added"></span>Added</li>
              <li>
                <span class="legend-swatch swatch-other"></span>Other request
              </li>
              <li><span class="legend-swatch swatch-holiday"></span>Holiday</li>
            </ul>
            <mat-calendar
              [startAt]="calendarStartAt"
              [minDate]="calendarMinDate"
              [maxDate]="calendarMaxDate"
              [selected]="null"
              [dateFilter]="isSelectableDay"
              [dateClass]="dayClass"
              (selectedChange)="onDayToggled($event)"
            ></mat-calendar>
          </div>

          <p class="picker-hint">
            Days can be added up to {{ maxExtensionDays }} days before or after
            this request, up to any neighbouring request.
          </p>

          @if (cancelBlocked) {
            <p class="edit-error-message" @expandCollapse>
              Removing every day cancels the request, which needs the approval
              permission. Restore a day, or ask an approver to cancel it.
            </p>
          }

          @if (overDayLimit) {
            <p class="edit-error-message" @expandCollapse>
              A request can cover at most {{ maxRequestDays }} days in one
              change. Remove some days before saving.
            </p>
          }

          @if (holidaysLoadFailed) {
            <p class="edit-error-message" @expandCollapse>
              Holidays could not be loaded, so the preview may group days
              differently than the server. Cancel and try again.
            </p>
          }

          @if (saveError) {
            <p class="edit-error-message" @expandCollapse>{{ saveError }}</p>
          }

          <div class="edit-actions">
            <button
              mat-stroked-button
              type="button"
              class="edit-cancel-button"
              [disabled]="isEditBusy"
              (click)="cancelEdit()"
            >
              Cancel
            </button>
            <button
              mat-raised-button
              type="submit"
              class="edit-save-button"
              [disabled]="!canSaveEdit"
            >
              @if (isSaving) {
                <mat-spinner diameter="16" class="button-spinner"></mat-spinner>
                Saving
              } @else if (isCheckingEditOverlap) {
                <mat-spinner diameter="16" class="button-spinner"></mat-spinner>
                Checking
              } @else {
                Save
              }
            </button>
          </div>
        </form>
      } @else if (dateRangeLabel || data.typeName) {
        <div class="request-meta" @fadeIn>
          @if (data.typeName) {
            <span class="meta-chip type-chip">{{ data.typeName }}</span>
          }
          @if (dateRangeLabel) {
            <span class="meta-dates">
              <mat-icon class="meta-icon">event</mat-icon>
              <span>{{ dateRangeLabel }}</span>
              @if (daysOffLabel) {
                <span class="meta-days">({{ daysOffLabel }})</span>
              }
            </span>
          }
        </div>
        <!-- Same carry-over box as the calendar's event dialog (PBI 6682 AC 4).
             Read view only: it leaves with the summary while editing. -->
        <app-time-off-carry-over-summary
          [carryOverDays]="data.carryOverDays"
          [daysOff]="data.daysOff ?? 0"
        />
      }
    </div>

    <!-- Notes and the manager note box belong to the read view; the edit view is the
         summary plus the day picker only. -->
    @if (!isEditing) {
      <!-- Overlap check (approvers, pending requests): who else on a shared matter is already off.
           Independent of the notes load, so it never waits on them. The live region stays in the
           DOM across the three states so screen readers announce each change. -->
      <div class="overlap-status" aria-live="polite">
        @if (isCheckingOverlap) {
          <p class="overlap-hint" @expandCollapse>
            Checking for overlapping time off…
          </p>
        } @else if (overlapCheckFailed) {
          <p class="overlap-hint" @expandCollapse>Overlap check unavailable</p>
        } @else if (showOverlapWarning) {
          <div class="overlap-warning" @expandCollapse>
            <mat-icon class="overlap-warning__icon">warning</mat-icon>
            <div class="overlap-warning__body">
              <h3 class="overlap-warning__title">Overlapping time off</h3>
              <p class="overlap-warning__intro">
                Resources in the same practice area and on the same matter are
                already off on these dates:
              </p>
              <ul class="overlap-warning__list" role="list">
                @for (row of overlapRows; track row.timeOffRequestId) {
                  <li class="overlap-row">
                    <span class="overlap-row__name">{{
                      row.employeeName
                    }}</span>
                    <span
                      class="overlap-row__status"
                      [class.overlap-row__status--pending]="row.isPending"
                      >{{ row.statusName }}</span
                    >
                    <span class="overlap-row__days">{{ row.daysLabel }}</span>
                    @if (row.mattersLabel) {
                      <span class="overlap-row__matters">{{
                        row.mattersLabel
                      }}</span>
                    }
                  </li>
                }
              </ul>
            </div>
          </div>
        }
      </div>

      @if (isLoading) {
        <div class="loading-state">
          <mat-spinner diameter="32"></mat-spinner>
        </div>
      } @else {
        @if (requesterNotes?.notes) {
          <section class="note-section">
            <h3 class="note-section-title">Requestor Note</h3>
            <div class="note-body" [innerHTML]="requesterNotes?.notes"></div>
          </section>
        }

        @if (hasManagerHeader) {
          <section class="note-section">
            <h3 class="note-section-title manager-note-title">
              @if (managerNoteStatusLabel) {
                <span
                  class="status-badge"
                  [ngClass]="managerNoteStatusBadgeClass"
                  >{{ managerNoteStatusLabel }}</span
                >
              }
              <span class="manager-note-meta">{{ managerNoteHeader }}</span>
            </h3>
            @if (managerNotes?.notes) {
              <div class="note-body" [innerHTML]="managerNotes?.notes"></div>
            }
          </section>
        }

        @if (hasCancellationHeader) {
          <section class="note-section">
            <h3 class="note-section-title manager-note-title">
              @if (cancellationNoteStatusLabel) {
                <span
                  class="status-badge"
                  [ngClass]="cancellationNoteStatusBadgeClass"
                  >{{ cancellationNoteStatusLabel }}</span
                >
              }
              <span class="manager-note-meta">{{ cancellationNoteHeader }}</span>
            </h3>
            @if (cancellationNotes?.notes) {
              <div class="note-body" [innerHTML]="cancellationNotes?.notes"></div>
            }
          </section>
        }

        @if (!hasAnyNotes && !showManagerNoteInput) {
          <div class="empty-state">
            <mat-icon class="empty-icon">sticky_note_2</mat-icon>
            <p class="empty-message">No notes have been added.</p>
          </div>
        }

        @if (showManagerNoteInput) {
          <section class="note-section manager-note-input-section">
            <h3 class="note-section-title">
              Manager Note
              @if (showActions) {
                <span class="note-label-hint">
                  (required to return, optional to approve)
                </span>
              } @else {
                <span class="note-label-hint"> (optional) </span>
              }
            </h3>
            <textarea
              class="manager-note-textarea"
              [class.has-error]="noteError"
              [(ngModel)]="managerNoteInput"
              (ngModelChange)="onNoteInputChange()"
              rows="5"
              placeholder="Add a note for the requestor..."
            ></textarea>
            @if (noteError) {
              <p class="note-error-message">
                A note is required when returning a request.
              </p>
            }
          </section>
        }
      }
    }
  </mat-dialog-content>

  @if (!isEditing) {
    @if (showActions) {
      <div class="dialog-actions">
        <button mat-stroked-button class="return-button" (click)="return()">
          Return
        </button>
        <button mat-raised-button class="approve-button" (click)="approve()">
          Approve
        </button>
      </div>
    } @else if (showCancelAction) {
      <div class="dialog-actions">
        <button
          mat-stroked-button
          type="button"
          class="cancel-request-button"
          (click)="cancelRequest()"
        >
          Cancel Time Off
        </button>
      </div>
    }
  }
</div>
